import { SxProps, Theme } from "@mui/material";

export const modalStyles: SxProps<Theme> = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  borderRadius: "16px",
  boxShadow: 24,
  background: "none",
  width: { xs: "90%", sm: "90%", md: "90%" },
};

export const headerBoxStyles: SxProps<Theme> = (theme) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: { xs: 1, sm: 2 },
  minWidth: 0,
  background: theme.palette.modalColors.headerColor,
  paddingY: "16px",
  paddingX: { xs: "16px", sm: "38px" },
  borderTopLeftRadius: "16px",
  borderTopRightRadius: "16px",
});

export const headerRightBoxStyles: SxProps<Theme> = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: { xs: "12px", sm: "30px" },
  flex: "0 1 auto",
  minWidth: 0,
};

export const headerContentStyles = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
};

export const contentStyles: SxProps<Theme> = (theme) => ({
  display: "flex",
  gap: "16px",
  flexDirection: "column",
  backgroundColor: theme.palette.modalColors.bgColor,
  padding: "22px",
  borderBottomLeftRadius: "16px",
  borderBottomRightRadius: "16px",
  overflowY: "auto",
  maxHeight: { xs: "80dvh", sm: "80dvh", md: "80dvh" },
});

export const htmlContentStyles: SxProps<Theme> = (theme) => ({
  mb: 3,
  color: theme.palette.text.primary,
  "& p, & div, & span": {
    color: theme.palette.text.primary,
    marginBottom: "8px",
  },
  "& table": {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    backgroundColor: theme.palette.background.paper,
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: theme.shadows[2],
  },
  "& th, & td": {
    border: "none",
    padding: "16px 12px",
    textAlign: "left",
    color: theme.palette.text.primary,
    fontSize: "0.875rem",
    lineHeight: 1.4,
  },
  "& td": {
    padding: "18px 16px",
  },

  "& th": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? theme.palette.primary.main
        : theme.palette.grey[100],
    fontWeight: 700,
    color:
      theme.palette.mode === "dark"
        ? theme.palette.primary.contrastText
        : theme.palette.grey[800],
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    fontSize: "0.75rem",
    position: "sticky",
    top: 0,
    zIndex: 1,
    padding: "20px 16px",
    "&:first-of-type": {
      borderTopLeftRadius: "12px",
    },
    "&:last-of-type": {
      borderTopRightRadius: "12px",
    },
  },
  "& tr:nth-of-type(even)": {
    backgroundColor: theme.palette.action.hover,
  },
  "& tr:hover": {
    backgroundColor: theme.palette.action.selected,
    transition: "background-color 0.2s ease-in-out",
  },
  "& tr:last-child td:first-of-type": {
    borderBottomLeftRadius: "12px",
  },
  "& tr:last-child td:last-of-type": {
    borderBottomRightRadius: "12px",
  },

  ...(theme.palette.mode === "dark" && {
    '& *[style*="color: red"], & *[style*="color:#ff0000"], & *[style*="color:#FF0000"], & *[style*="color: #dc2626"], & *[style*="color:#dc2626"]':
      {
        color: "#ff6b6b !important",
      },
    '& *[style*="background-color: red"], & *[style*="background-color:#ff0000"], & *[style*="background-color:#FF0000"], & *[style*="background-color: #dc2626"], & *[style*="background-color:#dc2626"]':
      {
        backgroundColor: "#ff6b6b !important",
        color: "#ffffff !important",
      },

    '& *[style*="color: green"], & *[style*="color:#00ff00"], & *[style*="color:#00FF00"], & *[style*="color: #059669"], & *[style*="color:#059669"]':
      {
        color: "#51cf66 !important",
      },
    '& *[style*="background-color: green"], & *[style*="background-color:#00ff00"], & *[style*="background-color:#00FF00"], & *[style*="background-color: #059669"], & *[style*="background-color:#059669"]':
      {
        backgroundColor: "#51cf66 !important",
        color: "#ffffff !important",
      },

    '& td[style*="color: red"], & td[style*="color:#ff0000"], & td[style*="color: #dc2626"]':
      {
        color: "#ff6b6b !important",
        fontWeight: "600 !important",
      },
    '& td[style*="color: green"], & td[style*="color:#00ff00"], & td[style*="color: #059669"]':
      {
        color: "#51cf66 !important",
        fontWeight: "600 !important",
      },

    "& .red, & .error, & .danger": {
      color: "#ff6b6b !important",
      fontWeight: "600 !important",
    },
    "& .green, & .success, & .active": {
      color: "#51cf66 !important",
      fontWeight: "600 !important",
    },

    '& *[style*="#dc2626"], & *[style*="#DC2626"]': {
      color: "#ff6b6b !important",
      fontWeight: "600 !important",
    },
    '& *[style*="#059669"], & *[style*="#059669"]': {
      color: "#51cf66 !important",
      fontWeight: "600 !important",
    },

    '& *[style*="rgb(220, 38, 38)"]': {
      color: "#ff6b6b !important",
      fontWeight: "600 !important",
    },
    '& *[style*="rgb(5, 150, 105)"]': {
      color: "#51cf66 !important",
      fontWeight: "600 !important",
    },

    '& font[color="red"], & font[color="#ff0000"], & font[color="#FF0000"]': {
      color: "#ff6b6b !important",
      fontWeight: "600 !important",
    },
    '& font[color="green"], & font[color="#00ff00"], & font[color="#00FF00"]': {
      color: "#51cf66 !important",
      fontWeight: "600 !important",
    },
  }),
});

export const imageStyles = {
  width: "100%",
  height: "auto",
  borderRadius: "8px",
};

export const sectionTitleStyles: SxProps<Theme> = (theme) => ({
  mb: 2,
  color: theme.palette.text.primary,
  fontWeight: 600,
});

export const cardStyles: SxProps<Theme> = (theme) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  "&:hover": {
    boxShadow: theme.shadows[4],
  },
});

export const cardContentStyles: SxProps<Theme> = (theme) => ({
  "& .MuiTypography-subtitle1": {
    color: theme.palette.text.primary,
    fontWeight: 600,
  },
  "& .MuiTypography-body2": {
    color: theme.palette.text.secondary,
  },
});

export const tableContainerStyles: SxProps<Theme> = (theme) => ({
  "&::-webkit-scrollbar": {
    width: "8px",
    height: "8px",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: theme.palette.divider,
    borderRadius: "4px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: theme.palette.text.disabled,
    borderRadius: "4px",
    "&:hover": {
      backgroundColor: theme.palette.text.secondary,
    },
  },
});
